import { EditorChangeType } from '../types';
import { AbstractCommand } from './AbstractCommand';

import type { Element } from '../models/Element';
import type { Track } from '../models/Track';
import type { VideoEditor } from '../VideoEditor';

export class RemoveTrackCommand extends AbstractCommand {
  private trackId: string;

  getName(): string {
    return 'RemoveTrackCommand';
  }

  private removedTrack?: Track;

  private trackElements: Element[] = [];

  private _shouldCreateUndoRedoEntry: boolean;

  constructor(
    editor: VideoEditor,
    trackOrId: Track | string,
    createUndoRedoEntry: boolean = true,
  ) {
    super(editor);
    this.trackId = typeof trackOrId === 'string' ? trackOrId : trackOrId.id;
    this._shouldCreateUndoRedoEntry = createUndoRedoEntry;
    // Store the track for undo/redo operations
    this.removedTrack =
      typeof trackOrId === 'string'
        ? this.editor.getTrackById(trackOrId)
        : trackOrId;
  }

  shouldCreateUndoRedoEntry(): boolean {
    return this._shouldCreateUndoRedoEntry;
  }

  execute(): boolean {
    if (!this.removedTrack) return false;

    // Store all elements in the track for undo operations
    this.trackElements =
      this.editor.timeline.getTrack(this.trackId)?.mutableElements || [];

    const success = this.editor.timeline.removeTrack(this.trackId);
    if (success) {
      this.recalculateTimeline();
      this.editor.markDirty('remove_track_command');
      this.editor.notifyObservers(EditorChangeType.TRACK_REMOVED, {
        trackId: this.trackId,
        track: this.removedTrack,
      });
    }
    return success;
  }

  undo(): boolean {
    if (!this.removedTrack) return false;

    // Re-add the track
    const success = this.editor.timeline.addTrack(this.removedTrack);
    if (success) {
      // Re-add all elements that were in the track
      for (const element of this.trackElements) {
        this.editor.timeline.addElement(this.trackId, element);
      }

      this.recalculateTimeline();
      this.editor.markDirty('remove_track_command_undo');
      this.editor.notifyObservers(EditorChangeType.TRACK_ADDED, {
        track: this.removedTrack,
      });

      // Notify about restored elements
      for (const element of this.trackElements) {
        this.editor.notifyObservers(EditorChangeType.ELEMENT_ADDED, {
          element,
          trackId: this.trackId,
        });
      }
    }
    return !!success;
  }

  redo(): boolean {
    return this.execute();
  }

  getDescription(): string {
    return `Remove track ${this.trackId}`;
  }

  getRemovedTrack(): Track | undefined {
    return this.removedTrack;
  }
}
